/**
 * CategoryConfigEditor — per-category overrides for Base Price + Bid
 * Increment Tiers. Each category (A / B / C / D / Icon / Guest) can optionally
 * override the auction's global tiers/base. When the "Override" toggle is
 * OFF the auction global values are used.
 *
 * value shape: {
 *   A: { base_price: 10, max_points: 5000, max_bid: 50000, bid_increments: [{up_to,step}, ...] },
 *   B: { base_price: 20, bid_increments: null },
 *   ...
 * }
 *
 * Persists as `settings.category_config` on the auction.
 */
import React, { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import BidTiersEditor from "@/components/BidTiersEditor";

const CATEGORIES = ["A", "B", "C", "D", "Icon", "Guest"];

export default function CategoryConfigEditor({ value, onChange, globalBase, globalTiers, auctionId }) {
  const cfg = value || {};
  const [open, setOpen] = useState(null); // which category is expanded

  const setCat = (cat, patch) => {
    const next = { ...cfg };
    if (!next[cat]) next[cat] = {};
    next[cat] = { ...next[cat], ...patch };
    // If all fields are empty/null, drop the entry entirely so it falls back to global.
    const hasBase = next[cat].base_price != null && next[cat].base_price !== "";
    const hasTiers = !!next[cat].bid_increments;
    const hasMax = next[cat].max_points != null && next[cat].max_points !== "";
    if (!hasBase && !hasTiers && !hasMax) {
      delete next[cat];
    }
    onChange(next);
  };

  const clearCat = (cat) => {
    const next = { ...cfg };
    delete next[cat];
    onChange(next);
  };

  // Iter28: one-tap sync — first SAVES the current editor config (so values
  // typed but not yet saved via "Save Settings" are persisted), then rewrites
  // base_price for every player in the category (sold players keep sold_price).
  const [syncing, setSyncing] = useState(null);
  const syncCat = async (cat) => {
    if (!auctionId) return;
    setSyncing(cat);
    try {
      // Persist the latest editor state so the backend sees the typed values.
      const { data: auc } = await api.get(`/auctions/${auctionId}`);
      const settings = { ...(auc.settings || {}), category_config: cfg };
      await api.patch(`/auctions/${auctionId}`, { settings });
      const { data } = await api.post(`/auctions/${auctionId}/sync-category-base/${cat}`);
      toast.success(`Synced ${data.players_updated} players in Category ${cat} to ${data.base_price} pts`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Sync failed");
    } finally {
      setSyncing(null);
    }
  };

  return (
    <div className="border border-gray-200 rounded p-4 bg-white" data-testid="category-config-editor">
      <div className="label-mono mb-1">CATEGORY OVERRIDES · base price & bid increments per category</div>
      <p className="text-xs text-gray-500 mb-3">
        Leave a category untouched to inherit the auction-wide values ({globalBase} pts base). Override any category to
        use its own base price and tiered bid increments — those are honored when a player of that category is on the
        block during the live auction.
      </p>
      <div className="space-y-2">
        {CATEGORIES.map((cat) => {
          const entry = cfg[cat] || {};
          const overridden = !!(entry.base_price || entry.bid_increments || entry.max_points);
          const isOpen = open === cat;
          return (
            <div key={cat} className="border border-gray-200 rounded" data-testid={`cat-cfg-${cat}`}>
              <div className="flex items-center gap-3 px-3 py-2 bg-gray-50">
                <div className={`w-8 h-8 grid place-items-center rounded font-black text-white ${cat === "Icon" ? "bg-amber-600" : cat === "A" ? "bg-blue-600" : cat === "B" ? "bg-emerald-600" : cat === "C" ? "bg-orange-600" : cat === "D" ? "bg-fuchsia-600" : "bg-slate-500"}`}>
                  {cat === "Icon" ? "★" : cat === "Guest" ? "G" : cat}
                </div>
                <div className="flex-1">
                  <div className="font-bold uppercase text-sm tracking-widest">Category {cat}</div>
                  <div className="text-[11px] text-gray-500">
                    {overridden
                      ? `Custom · base ${entry.base_price ?? "—"} · max/team ${entry.max_points ?? "∞"} · max bid ${entry.max_bid ?? "∞"} · ${entry.bid_increments?.length || 0} tier(s)`
                      : `Inherits global · base ${globalBase} pts`}
                  </div>
                </div>
                {overridden && (
                  <button
                    type="button"
                    onClick={() => clearCat(cat)}
                    className="text-[11px] font-bold uppercase tracking-widest text-red-600 hover:text-red-700"
                    data-testid={`cat-cfg-reset-${cat}`}
                  >Reset</button>
                )}
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? null : cat)}
                  className="text-xs font-bold uppercase tracking-widest px-3 py-1 rounded border border-gray-300 hover:border-orange-500 hover:text-orange-600"
                  data-testid={`cat-cfg-toggle-${cat}`}
                >{isOpen ? "Close" : (overridden ? "Edit" : "Override")}</button>
              </div>
              {isOpen && (
                <div className="p-3 space-y-3 border-t border-gray-200">
                  <div className="flex items-center gap-3 flex-wrap">
                    <label className="label-mono">Base Price (pts)</label>
                    <input
                      type="number"
                      min="1"
                      value={entry.base_price ?? ""}
                      placeholder={String(globalBase ?? 10)}
                      onChange={(e) => setCat(cat, { base_price: e.target.value === "" ? null : Number(e.target.value) })}
                      className="w-32 bg-white border border-gray-200 rounded px-2 py-1 text-sm focus:border-orange-500 outline-none"
                      data-testid={`cat-cfg-base-${cat}`}
                    />
                    <span className="text-[10px] text-gray-500">Empty → use global {globalBase}</span>
                    {entry.base_price != null && entry.base_price !== "" && (
                      <button
                        type="button"
                        onClick={() => syncCat(cat)}
                        disabled={syncing === cat}
                        className="text-[11px] font-bold uppercase tracking-widest px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-500 text-white disabled:opacity-50"
                        data-testid={`cat-cfg-sync-${cat}`}
                        title="Rewrite base_price for every player in this category to match the configured value (sold players keep their sold price)"
                      >
                        {syncing === cat ? "Syncing…" : "Sync to all players"}
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <label className="label-mono">Max Points / Team (pts)</label>
                    <input
                      type="number"
                      min="0"
                      value={entry.max_points ?? ""}
                      placeholder="No cap"
                      onChange={(e) => setCat(cat, { max_points: e.target.value === "" ? null : Number(e.target.value) })}
                      className="w-32 bg-white border border-gray-200 rounded px-2 py-1 text-sm focus:border-orange-500 outline-none"
                      data-testid={`cat-cfg-max-${cat}`}
                    />
                    <span className="text-[10px] text-gray-500">Per-team spend cap on this category — blocks bids once reached.</span>
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <label className="label-mono">Max Bid / Player (pts)</label>
                    <input
                      type="number"
                      min="0"
                      value={entry.max_bid ?? ""}
                      placeholder="No cap"
                      onChange={(e) => setCat(cat, { max_bid: e.target.value === "" ? null : Number(e.target.value) })}
                      className="w-32 bg-white border border-gray-200 rounded px-2 py-1 text-sm focus:border-orange-500 outline-none"
                      data-testid={`cat-cfg-max-bid-${cat}`}
                    />
                    <span className="text-[10px] text-gray-500">Hard ceiling — no single player of this category can be sold for more.</span>
                  </div>
                  <div className="pt-2 border-t border-dashed border-gray-200">
                    <BidTiersEditor
                      value={entry.bid_increments || globalTiers}
                      onChange={(tiers) => setCat(cat, { bid_increments: tiers })}
                    />
                    <button
                      type="button"
                      onClick={() => setCat(cat, { bid_increments: null })}
                      className="mt-2 text-[11px] uppercase tracking-widest text-gray-500 hover:text-red-600"
                      data-testid={`cat-cfg-clear-tiers-${cat}`}
                    >Clear tiers · inherit global</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
